/**
 * Kiosk Service API Contracts
 * Request/Response schemas for kiosk hardware management, sessions, and transactions
 *
 * PCI-DSS P2PE compliance:
 *   - Payment card data MUST NOT appear in any request/response schema here
 *   - All payment processing is delegated to payment-service
 *   - Only transaction references and amounts pass through kiosk-service
 *
 * Auth: X-Kiosk-API-Key header (hardware units) + JWT (admin endpoints)
 */

import { CustomerIdSchema } from '@autopia/shared-types';
import { z } from 'zod';

// KioskPaymentMethodSchema lives in a leaf module to avoid a circular import
// (see payment-method.ts). Imported here for local use and re-exported so
// `@autopia/api-contracts/kiosk` consumers keep resolving it from this barrel.
// The walk-up request's attraction attachments are the same shape ticketing
// takes on order creation. Deep-imported from its leaf module rather than the
// ../ticketing barrel, which re-exports kiosk-inter-service.ts and would cycle
// back through this file. Not re-exported: it stays owned by `/ticketing`, so
// the root barrel has exactly one export of the name.
import { OrderAttractionAttachmentSchema } from '../ticketing/attraction-attachment';

import { KioskPaymentMethodSchema, type KioskPaymentMethod } from './payment-method';

export { KioskPaymentMethodSchema, type KioskPaymentMethod };

// ============================================================================
// Kiosk Hardware Schemas
// ============================================================================

export const KioskStatusSchema = z.enum([
  'online',
  'offline',
  'maintenance',
  'degraded',
  'tamper_alert',
]);

export type KioskStatus = z.infer<typeof KioskStatusSchema>;

export const KioskHardwareStatusSchema = z.object({
  printerStatus: z.enum(['ok', 'paper_low', 'error', 'offline']),
  scannerStatus: z.enum(['ok', 'error', 'offline']),
  paymentTerminalStatus: z.enum(['ok', 'error', 'offline', 'tamper_alert']),
  displayStatus: z.enum(['ok', 'error']),
  cashDrawerStatus: z.enum(['closed', 'open', 'error']).optional(),
  temperatureCelsius: z.number().optional(),
  diskUsagePercent: z.number().min(0).max(100).optional(),
  memoryUsagePercent: z.number().min(0).max(100).optional(),
});

export type KioskHardwareStatus = z.infer<typeof KioskHardwareStatusSchema>;

export const KioskDetailSchema = z.object({
  id: z.string().uuid(),
  kioskId: z.string().max(50),
  kioskName: z.string().max(100),
  serialNumber: z.string().max(100),
  locationZone: z.string().max(50).nullable(),
  locationDescription: z.string().max(200).nullable(),
  status: KioskStatusSchema,
  softwareVersion: z.string().max(50),
  hardwareStatus: KioskHardwareStatusSchema.nullable(),
  lastHeartbeatAt: z.string().datetime().nullable(),
  lastTransactionAt: z.string().datetime().nullable(),
  transactionsToday: z.number().int().nonnegative(),
  revenueIdrToday: z.number().int().nonnegative(),
  isActive: z.boolean(),
  registeredAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type KioskDetail = z.infer<typeof KioskDetailSchema>;

// ============================================================================
// Kiosk Registration (admin)
// ============================================================================

/**
 * Kiosk lifecycle status — mirrors the KioskStatus enum in kiosk-service Prisma.
 * Distinct from KioskStatusSchema, which models real-time operational health.
 */
export const KioskDeviceStatusSchema = z.enum([
  'INACTIVE',
  'PENDING_ACTIVATION',
  'ACTIVE',
  'MAINTENANCE',
  'OFFLINE',
  'DECOMMISSIONED',
]);

export type KioskDeviceStatus = z.infer<typeof KioskDeviceStatusSchema>;

/**
 * Kiosk device type — mirrors the DeviceType enum in kiosk-service Prisma.
 */
export const KioskDeviceTypeSchema = z.enum([
  'STANDARD',
  'CREDIT_TOPUP_ONLY',
  'TICKET_ONLY',
  'EXIT_PHOTO',
]);

export type KioskDeviceType = z.infer<typeof KioskDeviceTypeSchema>;

export const KioskRegistrationRequestSchema = z.object({
  /**
   * The terminal's identity-service `User.id` — the `KIOSK`-role staff account
   * created for it. **Required since #843.**
   *
   * Registration used to omit it, and that is why unlinked terminals existed:
   * `KioskResolverService.resolveKiosk` looks the device up *by* `userId`
   * (`kiosk-resolver.service.ts:15`), so a kiosk registered without one has no
   * principal to issue a JWT for and cannot reach a walk-up route at all. It
   * appeared in the admin list, sent heartbeats, and could not sell anything —
   * and fixing it took direct database access, because nothing wrote the column.
   *
   * Registration is therefore **identity-first**: create the `KIOSK` staff
   * account, then register the hardware against it. `POST /kiosks/:id/link-identity`
   * exists for terminals registered before this was required.
   *
   * Derives from `CustomerIdSchema` rather than restating the shape (AGENTS.md
   * Rule #7c) — it is the same `User.id` vocabulary ADR-037 governs.
   */
  userId: CustomerIdSchema,
  serialNumber: z.string().min(1).max(100),
  location: z.string().min(1).max(200),
  zoneId: z.string().min(1).max(100),
  deviceType: KioskDeviceTypeSchema.optional(),
  capabilities: z.array(z.string()).optional(),
  hardwareConfig: z.record(z.string(), z.unknown()).optional(),
  softwareVersion: z.string().max(50).optional(),
});

export type KioskRegistrationRequest = z.infer<typeof KioskRegistrationRequestSchema>;

export const KioskRegistrationResponseSchema = z.object({
  kiosk: KioskDetailSchema,
  /** Provisioned API key — shown ONCE, store securely on hardware */
  apiKey: z.string().max(256),
  message: z.string(),
});

export type KioskRegistrationResponse = z.infer<typeof KioskRegistrationResponseSchema>;

/**
 * `POST /api/v1/kiosks/:id/link-identity` — attach a `KIOSK` staff account to a
 * terminal registered before #843 made the link mandatory.
 *
 * A separate endpoint rather than a `userId` field on `UpdateKioskRequestSchema`,
 * deliberately. A general update is the wrong shape for this: re-pointing a live
 * terminal's identity would silently reassign every future walk-up sale, and it
 * is the kind of field that gets included by a `...dto` spread. This does one
 * thing, refuses when the terminal already has an identity, and is idempotent
 * when the link it is asked to make already exists.
 */
export const LinkKioskIdentityRequestSchema = z.object({
  userId: CustomerIdSchema,
});

export type LinkKioskIdentityRequest = z.infer<typeof LinkKioskIdentityRequestSchema>;

/**
 * GET /kiosks/:id (public) -- verified against DevicesService.findById()'s
 * explicit Prisma `select`. Excludes apiKey/apiKeyHash (never selected there).
 */
export const KioskDeviceSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  serialNumber: z.string(),
  location: z.string(),
  zoneId: z.string(),
  status: KioskDeviceStatusSchema,
  deviceType: KioskDeviceTypeSchema,
  capabilities: z.array(z.string()),
  hardwareConfig: z.record(z.string(), z.unknown()),
  softwareVersion: z.string().nullable(),
  lastHeartbeatAt: z.string().nullable(),
  activatedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type KioskDevice = z.infer<typeof KioskDeviceSchema>;

// ============================================================================
// Kiosk Session Management
// ============================================================================

/**
 * Kiosk session type — mirrors the SessionType enum in kiosk-service Prisma.
 */
export const KioskSessionTypeSchema = z.enum([
  'CREDIT_TOPUP',
  'TICKET_PURCHASE',
  'BALANCE_CHECK',
  'INFORMATION',
]);

export type KioskSessionType = z.infer<typeof KioskSessionTypeSchema>;

/**
 * KioskSessionStartRequest — body for POST /kiosks/:kioskId/sessions/start.
 * The kiosk id is a path param; the body carries the optional wristband, the
 * session type, and free-form metadata.
 */
export const KioskSessionStartRequestSchema = z.object({
  wristbandId: z.string().optional(),
  sessionType: KioskSessionTypeSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type KioskSessionStartRequest = z.infer<typeof KioskSessionStartRequestSchema>;

/**
 * KioskSessionUpdateRequest — body for PUT /kiosks/:kioskId/sessions/:sessionId.
 * Only metadata is mutable on an active session.
 */
export const KioskSessionUpdateRequestSchema = z.object({
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type KioskSessionUpdateRequest = z.infer<typeof KioskSessionUpdateRequestSchema>;

/**
 * KioskSessionStartResponse — the cached session payload returned on start and
 * by GET /current.
 */
export const KioskSessionStartResponseSchema = z.object({
  sessionId: z.string(),
  kioskId: z.string(),
  sessionType: KioskSessionTypeSchema,
  status: z.string(),
  wristbandId: z.string().nullable(),
  visitorName: z.string().nullable(),
  currentBalance: z.number().nullable(),
  startedAt: z.string(),
  timeoutAt: z.string(),
});

export type KioskSessionStartResponse = z.infer<typeof KioskSessionStartResponseSchema>;

// ============================================================================
// Kiosk Payment Method (canonical definition in ./payment-method leaf module;
// imported at the top of this file and re-exported from there)
// ============================================================================

export const KioskQrisGatewaySchema = z.enum(['MIDTRANS', 'XENDIT', 'DOKU']);
export type KioskQrisGateway = z.infer<typeof KioskQrisGatewaySchema>;

// ============================================================================
// Kiosk Transaction (top-up / purchase / confirm)
// ============================================================================

/**
 * InitiateTopupRequest — body for POST /kiosks/:kioskId/transactions/topup.
 */
export const InitiateTopupRequestSchema = z.object({
  sessionId: z.string().optional(),
  wristbandId: z.string().min(1),
  amount: z.number().positive(),
  paymentMethod: KioskPaymentMethodSchema,
  edcBank: z.string().optional(),
});

export type InitiateTopupRequest = z.infer<typeof InitiateTopupRequestSchema>;

/**
 * InitiatePurchaseRequest — body for POST /kiosks/:kioskId/transactions/purchase.
 * wristbandId is required for in-park attraction purchases and omitted/null for
 * walk-up entry-ticket purchases.
 */
export const InitiatePurchaseRequestSchema = z.object({
  sessionId: z.string().optional(),
  wristbandId: z.string().nullable().optional(),
  attractionIds: z.array(z.string()).min(1),
  paymentMethod: KioskPaymentMethodSchema,
  amount: z.number().positive(),
  edcBank: z.string().optional(),
});

export type InitiatePurchaseRequest = z.infer<typeof InitiatePurchaseRequestSchema>;

/**
 * ConfirmTransactionRequest — body for
 * POST /kiosks/:kioskId/transactions/:txId/confirm.
 */
export const ConfirmTransactionRequestSchema = z.object({
  edcApprovalCode: z.string().optional(),
  reference: z.string().optional(),
});

export type ConfirmTransactionRequest = z.infer<typeof ConfirmTransactionRequestSchema>;

export const KioskTransactionResponseSchema = z.object({
  transactionId: z.string(),
  transactionNumber: z.string().optional(),
  status: z.string(),
  paymentDetails: z.record(z.string(), z.unknown()).optional(),
  attractionIds: z.array(z.string()).optional(),
  newBalance: z.number().optional(),
  qrisImageUrl: z.string().optional(),
});

export type KioskTransactionResponse = z.infer<typeof KioskTransactionResponseSchema>;

export const KioskTransactionTypeSchema = z.enum([
  'CREDIT_TOPUP',
  'TICKET_PURCHASE',
  'REFUND',
]);
export type KioskTransactionType = z.infer<typeof KioskTransactionTypeSchema>;

export const KioskTransactionStatusSchema = z.enum([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'PENDING_SYNC',
  'PENDING_REVIEW',
  'CREDIT_PENDING',
]);
export type KioskTransactionStatus = z.infer<typeof KioskTransactionStatusSchema>;

/**
 * GET /kiosks/:kioskId/transactions/:txId
 *
 * The service returns the KioskTransaction row spread verbatim
 * (`{ ...tx, bookingNumber: tx.transactionNumber }`), so this mirrors the Prisma
 * model field-for-field. `bookingNumber` aliases the non-nullable
 * `transactionNumber`, so it is always present. Prisma `DateTime` columns
 * serialize to ISO strings over HTTP.
 *
 * Note: this endpoint reports the raw persisted `status`. Only the *list*
 * endpoint remaps `COMPLETED` to `SUCCESS`; do not expect `SUCCESS` here.
 */
export const KioskTransactionStatusResponseSchema = z.object({
  id: z.string(),
  transactionNumber: z.string(),
  bookingNumber: z.string(),
  kioskId: z.string(),
  sessionId: z.string().nullable(),
  wristbandId: z.string().nullable(),
  transactionType: KioskTransactionTypeSchema,
  amount: z.number().int(),
  paymentMethod: KioskPaymentMethodSchema,
  paymentReference: z.string().nullable(),
  edcBank: z.string().nullable(),
  edcApprovalCode: z.string().nullable(),
  edcReferenceNumber: z.string().nullable(),
  status: KioskTransactionStatusSchema,
  offlineMode: z.boolean(),
  offlineLocalId: z.string().nullable(),
  syncedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});

export type KioskTransactionStatusResponse = z.infer<
  typeof KioskTransactionStatusResponseSchema
>;

export const KioskTransactionPollResultSchema = z.object({
  transactionId: z.string(),
  status: z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'CANCELLED', 'FAILED']),
  amount: z.number().optional(),
  paymentMethod: KioskPaymentMethodSchema.optional(),
  approvalCode: z.string().optional(),
  bookingNumber: z.string().optional(),
  reason: z.string().optional(),
});

export type KioskTransactionPollResult = z.infer<typeof KioskTransactionPollResultSchema>;

// ============================================================================
// Kiosk Heartbeat
// ============================================================================

export const KioskHeartbeatMetricsSchema = z.object({
  cpu: z.number(),
  memory: z.number(),
  disk: z.number(),
  network: z.number(),
});

export type KioskHeartbeatMetrics = z.infer<typeof KioskHeartbeatMetricsSchema>;

export const KioskHeartbeatPeripheralsSchema = z.object({
  printer: z.string(),
  paperLevel: z.number().optional(),
  scanner: z.string(),
  cashAcceptor: z.string(),
  edcTerminals: z.array(z.string()),
});

export type KioskHeartbeatPeripherals = z.infer<typeof KioskHeartbeatPeripheralsSchema>;

/**
 * KioskHeartbeatRequest — body for POST /kiosks/:kioskId/heartbeat.
 * The kiosk id is a path param; the body carries hardware metrics and
 * peripheral status.
 */
export const KioskHeartbeatRequestSchema = z.object({
  metrics: KioskHeartbeatMetricsSchema,
  peripherals: KioskHeartbeatPeripheralsSchema,
});

export type KioskHeartbeatRequest = z.infer<typeof KioskHeartbeatRequestSchema>;

/**
 * KioskReportStatusRequest — body for POST /kiosks/:kioskId/status.
 */
export const KioskReportStatusRequestSchema = z.object({
  status: KioskDeviceStatusSchema,
  reason: z.string().optional(),
});

export type KioskReportStatusRequest = z.infer<typeof KioskReportStatusRequestSchema>;

/**
 * KioskQueueCommandRequest — body for POST /kiosks/:kioskId/commands.
 */
export const KioskQueueCommandRequestSchema = z.object({
  command: z.string().min(1),
  payload: z.record(z.string(), z.unknown()).optional(),
});

export type KioskQueueCommandRequest = z.infer<typeof KioskQueueCommandRequestSchema>;

export const KioskHardwareMetricsResponseSchema = z.object({
  cpuUsage: z.number(),
  ramUsage: z.number(),
  storageUsed: z.number(),
  storageTotal: z.number(),
  networkStatus: z.enum(['ONLINE', 'OFFLINE']),
  lastHeartbeat: z.string().nullable(),
});

export type KioskHardwareMetrics = z.infer<typeof KioskHardwareMetricsResponseSchema>;

export const KioskPeripheralStatusSchema = z.enum(['OK', 'WARNING', 'ERROR']);
export type KioskPeripheralStatus = z.infer<typeof KioskPeripheralStatusSchema>;

export const KioskPeripheralDeviceSchema = z.object({
  status: KioskPeripheralStatusSchema,
  lastChecked: z.string().nullable(),
});

export const KioskPrinterDeviceSchema = KioskPeripheralDeviceSchema.extend({
  paperLevel: z.number(),
});

export const KioskPeripheralsResponseSchema = z.object({
  cashAcceptor: KioskPeripheralDeviceSchema,
  printer: KioskPrinterDeviceSchema,
  cardReader: KioskPeripheralDeviceSchema,
});

export type KioskPeripherals = z.infer<typeof KioskPeripheralsResponseSchema>;

export const KioskCashStatusResponseSchema = z.object({
  currentCashInDrawer: z.number(),
  lastCollectionAt: z.string().nullable(),
  lastCollectorName: z.string().nullable(),
});

export type KioskCashStatus = z.infer<typeof KioskCashStatusResponseSchema>;

export const KioskCommandResultSchema = z.object({
  success: z.boolean().optional(),
  commandId: z.string(),
});

export type KioskCommandResult = z.infer<typeof KioskCommandResultSchema>;

// ============================================================================
// Kiosk Pickup / Printing
// ============================================================================

export const KioskPickupResolveRequestSchema = z.object({
  qrContent: z.string().min(1),
  kioskId: z.string().min(1),
});

export type KioskPickupResolveRequest = z.infer<typeof KioskPickupResolveRequestSchema>;

export const KioskPickupPrintRequestSchema = z.object({
  orderNumber: z.string().min(1),
  kioskId: z.string().min(1),
});

export type KioskPickupPrintRequest = z.infer<typeof KioskPickupPrintRequestSchema>;

export const KioskPrintRequestSchema = z.object({
  qrContent: z.string().min(1),
  kioskId: z.string().min(1),
});

export type KioskPrintRequest = z.infer<typeof KioskPrintRequestSchema>;

export const KioskReprintRequestSchema = z.object({
  bookingNumber: z.string().min(1),
  kioskId: z.string().min(1),
  staffPin: z.string().min(1),
  reason: z.string().optional(),
});

export type KioskReprintRequest = z.infer<typeof KioskReprintRequestSchema>;

export const KioskGroupPrintWristbandSchema = z.object({
  wristbandId: z.string(),
  wristbandCode: z.string(),
  qrContent: z.string(),
  jobId: z.string().nullable(),
  status: z.string(),
  printedAt: z.string().nullable(),
});

export type KioskGroupPrintWristband = z.infer<typeof KioskGroupPrintWristbandSchema>;

export const KioskGroupPrintResultSchema = z.object({
  type: z.literal('group'),
  bookingNumber: z.string(),
  coordinatorName: z.string(),
  memberCount: z.number(),
  printed: z.number(),
  failed: z.number(),
  alreadyPrinted: z.boolean().optional(),
  lastPrintedAt: z.string().nullable().optional(),
  wristbands: z.array(KioskGroupPrintWristbandSchema),
});

export type KioskGroupPrintResult = z.infer<typeof KioskGroupPrintResultSchema>;

export const KioskIndividualPrintResultSchema = z.object({
  type: z.literal('individual'),
  wristbandId: z.string(),
  wristbandCode: z.string(),
  qrContent: z.string(),
  jobId: z.string(),
  status: z.string(),
});

export type KioskIndividualPrintResult = z.infer<typeof KioskIndividualPrintResultSchema>;

export const KioskPickupTicketSchema = z.object({
  ticketId: z.string(),
  ticketNumber: z.string(),
  ticketTypeName: z.string(),
  category: z.string().optional(),
  visitorName: z.string().nullable(),
});

export const KioskPickupAddonSchema = z.object({
  attractionCode: z.string(),
  attractionName: z.string(),
  slotStartTime: z.string().nullable(),
  slotEndTime: z.string().nullable(),
});

export const KioskPickupResolutionSchema = z.object({
  orderNumber: z.string(),
  orderId: z.string(),
  customerName: z.string(),
  customerEmail: z.string().nullable(),
  visitDate: z.string(),
  tickets: z.array(KioskPickupTicketSchema),
  addons: z.array(KioskPickupAddonSchema),
  alreadyPickedUp: z.boolean(),
  lastPickedUpAt: z.string().nullable(),
});

export type KioskPickupResolution = z.infer<typeof KioskPickupResolutionSchema>;

// ============================================================================
// Kiosk EDC / Offline Sync / Reconciliation
// ============================================================================

export const KioskEdcInitiateRequestSchema = z.object({
  amount: z.number().positive(),
  bankCode: z.string().min(1),
  localTransactionId: z.string().min(1),
});

export type KioskEdcInitiateRequest = z.infer<typeof KioskEdcInitiateRequestSchema>;

export const KioskEdcCallbackRequestSchema = z.object({
  edc_transaction_id: z.string().min(1),
  response_code: z.string().min(1),
  approval_code: z.string().optional(),
  card_last_four: z.string().optional(),
  amount: z.number().nonnegative(),
  status: z.string().min(1),
});

export type KioskEdcCallbackRequest = z.infer<typeof KioskEdcCallbackRequestSchema>;

export const KioskOfflineTransactionSchema = z.object({
  localId: z.string().min(1),
  transactionNumber: z.string().min(1),
  sessionId: z.string().optional(),
  wristbandId: z.string().optional(),
  transactionType: z.enum(['CREDIT_TOPUP', 'TICKET_PURCHASE']),
  amount: z.number().positive(),
  paymentMethod: z.enum(['CASH', 'QRIS']),
  createdAt: z.string().min(1),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type KioskOfflineTransaction = z.infer<typeof KioskOfflineTransactionSchema>;

export const KioskOfflineSyncRequestSchema = z.object({
  transactions: z.array(KioskOfflineTransactionSchema).max(100),
});

export type KioskOfflineSyncRequest = z.infer<typeof KioskOfflineSyncRequestSchema>;

export const KioskCollectCashRequestSchema = z.object({
  collectorId: z.string().min(1),
  witnessId: z.string().min(1),
  collectedAmount: z.number().nonnegative(),
  notes: z.string().optional(),
});

export type KioskCollectCashRequest = z.infer<typeof KioskCollectCashRequestSchema>;

export const KioskRecordCashCollectionRequestSchema = z.object({
  amountCollected: z.number().nonnegative(),
  collectorName: z.string().min(1),
  notes: z.string().optional(),
});

export type KioskRecordCashCollectionRequest = z.infer<
  typeof KioskRecordCashCollectionRequestSchema
>;

export const KioskCashCollectionResultSchema = z.object({
  success: z.boolean(),
  collectionId: z.string(),
});

export type KioskCashCollectionResult = z.infer<typeof KioskCashCollectionResultSchema>;

// ============================================================================
// Kiosk Admin Queries
// ============================================================================

export const GetKiosksQuerySchema = z.object({
  status: KioskDeviceStatusSchema.optional(),
  deviceType: KioskDeviceTypeSchema.optional(),
  zoneId: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
});

export type GetKiosksQuery = z.infer<typeof GetKiosksQuerySchema>;

export const GetReconciliationParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
});

export type GetReconciliationParams = z.infer<typeof GetReconciliationParamsSchema>;

export const GetTransactionsParamsSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
});

export type GetTransactionsParams = z.infer<typeof GetTransactionsParamsSchema>;

export const KioskAdminDeviceSchema = z.object({
  id: z.string(),
  serialNumber: z.string(),
  location: z.string(),
  zoneId: z.string(),
  status: KioskDeviceStatusSchema,
  softwareVersion: z.string().nullable(),
  lastHeartbeatAt: z.string().nullable(),
  createdAt: z.string(),
});

export type KioskAdminDevice = z.infer<typeof KioskAdminDeviceSchema>;

export const KioskStatusSummarySchema = z.object({
  total: z.number(),
  active: z.number(),
  offline: z.number(),
  maintenance: z.number(),
  inactive: z.number(),
  decommissioned: z.number(),
});

export type KioskStatusSummary = z.infer<typeof KioskStatusSummarySchema>;

export const KioskReconciliationSchema = z.object({
  id: z.string(),
  kioskId: z.string(),
  date: z.string(),
  expectedCash: z.number(),
  actualCash: z.number().nullable(),
  variance: z.number().nullable(),
  status: z.string(),
  cashCollectedAt: z.string().nullable(),
});

export type KioskReconciliation = z.infer<typeof KioskReconciliationSchema>;

export const KioskTransactionSchema = z.object({
  id: z.string(),
  kioskId: z.string().optional(),
  transactionType: z.string(),
  amountIdr: z.number(),
  paymentMethod: z.string(),
  status: z.string(),
  createdAt: z.string(),
});

export type KioskTransaction = z.infer<typeof KioskTransactionSchema>;

export const UpdateKioskRequestSchema = z.object({
  location: z.string().min(1).max(200).optional(),
  zoneId: z.string().min(1).max(100).optional(),
  deviceType: KioskDeviceTypeSchema.optional(),
  capabilities: z.array(z.string()).optional(),
  hardwareConfig: z.record(z.string(), z.unknown()).optional(),
  softwareVersion: z.string().max(50).optional(),
});

export type UpdateKioskRequest = z.infer<typeof UpdateKioskRequestSchema>;

// ============================================================================
// Kiosk Transaction History (admin)
// ============================================================================

export const GetKioskTransactionsQuerySchema = z.object({
  kioskId: z.string().max(50).optional(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  status: z.enum(['completed', 'failed', 'pending']).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetKioskTransactionsQuery = z.infer<typeof GetKioskTransactionsQuerySchema>;

export const KioskTransactionSummarySchema = z.object({
  transactionId: z.string().uuid(),
  kioskId: z.string().max(50),
  kioskName: z.string().max(100),
  status: z.enum(['completed', 'failed', 'pending']),
  totalIdr: z.number().int(),
  paymentMethod: z.string(),
  itemCount: z.number().int(),
  customerId: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  completedAt: z.string().datetime().nullable(),
});

export type KioskTransactionSummary = z.infer<typeof KioskTransactionSummarySchema>;

// ============================================================================
// Kiosk Statistics
// ============================================================================

export const KioskDailyStatsSchema = z.object({
  kioskId: z.string().max(50),
  kioskName: z.string().max(100),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totalTransactions: z.number().int(),
  completedTransactions: z.number().int(),
  failedTransactions: z.number().int(),
  totalRevenueIdr: z.number().int(),
  avgTransactionValueIdr: z.number(),
  uptime: z.object({
    totalMinutes: z.number().int(),
    onlineMinutes: z.number().int(),
    uptimePercentage: z.number(),
  }),
  paymentMethodBreakdown: z.record(z.string(), z.number().int()),
});

export type KioskDailyStats = z.infer<typeof KioskDailyStatsSchema>;

// ============================================================================
// Walk-Up Purchase  (kiosk app ↔ kiosk-service contract)
// ============================================================================

export const WalkUpItemSchema = z.object({
  ticketTypeCode: z.string(),
  quantity: z.number().int().positive(),
  visitorName: z.string().optional(),
  slotId: z.string().nullable().optional(),
  slotStartTime: z.string().optional(),
  slotEndTime: z.string().optional(),
});
export type WalkUpItem = z.infer<typeof WalkUpItemSchema>;

export const WalkUpBundleItemSchema = z.object({
  ticketTypeCode: z.string(),
  category: z.string(),
  quantity: z.number().int().positive(),
});
export type WalkUpBundleItem = z.infer<typeof WalkUpBundleItemSchema>;

export const WalkUpBundleSchema = z.object({
  bundleId: z.string().uuid(),
  bundleCode: z.string(),
  quantity: z.number().int().positive(),
  bundlePriceIdr: z.number().int().nonnegative(),
  items: z.array(WalkUpBundleItemSchema),
});
export type WalkUpBundle = z.infer<typeof WalkUpBundleSchema>;

export const InitiateWalkUpRequestSchema = z.object({
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  customerName: z.string(),
  customerEmail: z.string().email(),
  /** Parent tickets only. Attractions go in `attractionAttachments`. */
  items: z.array(WalkUpItemSchema),
  bundles: z.array(WalkUpBundleSchema).optional(),
  /**
   * ATTRACTION selections, each tied to the parent ticket instance the visitor
   * attached it to at the kiosk. Forwarded verbatim to ticketing-service, which
   * needs the mapping to give every attraction ticket a parent (#862).
   */
  attractionAttachments: z.array(OrderAttractionAttachmentSchema).optional(),
  totalIdr: z.number().positive(),
  paymentMethod: KioskPaymentMethodSchema,
  edcBank: z.string().optional(),
  successRedirectUrl: z.string().url(),
});
export type InitiateWalkUpRequest = z.infer<typeof InitiateWalkUpRequestSchema>;

export const WalkUpPaymentDetailsSchema = z.object({
  // Midtrans Snap
  snapToken: z.string().nullable().optional(),
  redirectUrl: z.string().nullable().optional(),
  clientKey: z.string().nullable().optional(),
  snapScriptUrl: z.string().nullable().optional(),
  // Legacy QRIS (backwards compat)
  qrisUrl: z.string().nullable().optional(),
  qrisToken: z.string().nullable().optional(),
  // EDC
  edcResult: z.object({
    referenceNumber: z.string().optional(),
    terminalId: z.string().optional(),
    status: z.string().optional(),
  }).optional(),
  // Direct QRIS — Xendit / Doku
  qrPayload: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  gatewayProvider: KioskQrisGatewaySchema.optional(),
  gatewayTransactionId: z.string().optional(),
});
export type WalkUpPaymentDetails = z.infer<typeof WalkUpPaymentDetailsSchema>;

export const InitiateWalkUpResponseSchema = z.object({
  transactionId: z.string(),
  transactionNumber: z.string(),
  orderId: z.string(),
  orderNumber: z.string(),
  totalIdr: z.number(),
  status: z.enum(['PENDING', 'PROCESSING']),
  paymentMethod: KioskPaymentMethodSchema,
  paymentDetails: WalkUpPaymentDetailsSchema,
  orderExpiresAt: z.string().nullable().optional(),
});
export type InitiateWalkUpResponse = z.infer<typeof InitiateWalkUpResponseSchema>;

export const ConfirmWalkUpRequestSchema = z.object({
  edcApprovalCode: z.string().optional(),
  paymentReference: z.string().optional(),
});
export type ConfirmWalkUpRequest = z.infer<typeof ConfirmWalkUpRequestSchema>;

export const ConfirmWalkUpResponseSchema = z.object({
  transactionId: z.string(),
  transactionNumber: z.string(),
  orderId: z.string().nullable(),
  status: z.string(),
  completedAt: z.string().nullable(),
  amount: z.number(),
  paymentMethod: KioskPaymentMethodSchema,
});
export type ConfirmWalkUpResponse = z.infer<typeof ConfirmWalkUpResponseSchema>;

export const WalkUpOrderItemSchema = z.object({
  id: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: z.string(),
  quantity: z.number().int(),
  unitPriceIdr: z.number(),
  totalIdr: z.number(),
  visitorName: z.string().nullable().optional(),
  visitorEmail: z.string().nullable().optional(),
});
export type WalkUpOrderItem = z.infer<typeof WalkUpOrderItemSchema>;

export const WalkUpOrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  status: z.string(),
  visitDate: z.string(),
  subtotalIdr: z.number(),
  totalIdr: z.number(),
  customerName: z.string().nullable().optional(),
  customerEmail: z.string().nullable().optional(),
  items: z.array(WalkUpOrderItemSchema).optional(),
  paidAt: z.string().nullable().optional(),
  fulfilledAt: z.string().nullable().optional(),
  _count: z.object({ tickets: z.number().int() }).optional(),
  metadata: z.object({
    fulfillment: z.object({
      ticketCount: z.number().int().optional(),
      pdfGenerated: z.boolean().optional(),
      pdfFilename: z.string().optional(),
      fulfilledAt: z.string().optional(),
    }).optional(),
  }).optional(),
});
export type WalkUpOrder = z.infer<typeof WalkUpOrderSchema>;

export const WalkUpOrderEnvelopeSchema = z.object({
  transactionId: z.string(),
  transactionNumber: z.string(),
  status: z.string(),
  paymentMethod: z.string().optional(),
  amount: z.number().optional(),
  order: WalkUpOrderSchema.nullable(),
});
export type WalkUpOrderEnvelope = z.infer<typeof WalkUpOrderEnvelopeSchema>;

// ============================================================================
// Inter-Service: kiosk-service → payment-service  (POST /payments/qris)
// ============================================================================

export const KioskQrisRequestSchema = z.object({
  transactionId: z.string().min(1),
  amount: z.number().positive(),
  currency: z.string().optional(),
});
export type KioskQrisRequest = z.infer<typeof KioskQrisRequestSchema>;

// ============================================================================
// Inter-Service: kiosk-service → payment-service  (POST /payments/snap)
// ============================================================================

export const KioskSnapRequestSchema = z.object({
  transactionId: z.string().min(1),
  amount: z.number().positive(),
  orderId: z.string().min(1),
  orderNumber: z.string().optional(),
  gateway: KioskQrisGatewaySchema.optional(),
  successRedirectUrl: z.string().url(),
});
export type KioskSnapRequest = z.infer<typeof KioskSnapRequestSchema>;

export const KioskSnapResponseSchema = z.discriminatedUnion('gatewayProvider', [
  z.object({
    gatewayProvider: z.literal('MIDTRANS'),
    snapToken: z.string(),
    redirectUrl: z.string().optional(),
    clientKey: z.string(),
    snapScriptUrl: z.string(),
    gatewayTransactionId: z.string().optional(),
  }),
  z.object({
    gatewayProvider: z.literal('XENDIT'),
    qrPayload: z.string(),
    expiresAt: z.string().optional(),
    gatewayTransactionId: z.string().optional(),
  }),
  z.object({
    gatewayProvider: z.literal('DOKU'),
    qrPayload: z.string(),
    expiresAt: z.string().optional(),
    gatewayTransactionId: z.string().optional(),
  }),
]);
export type KioskSnapResponse = z.infer<typeof KioskSnapResponseSchema>;

export const KioskPaymentStatusResponseSchema = z.object({
  status: z.string(),
});
export type KioskPaymentStatusResponse = z.infer<typeof KioskPaymentStatusResponseSchema>;

// ============================================================================
// Wristband Addon Purchase  (kiosk-service: /kiosks/:id/wristband/:id/addon-purchase)
// ============================================================================

export const WristbandAddonItemSchema = z.object({
  attractionId: z.string(),
  attractionCode: z.string(),
  ticketTypeId: z.string(),
  ticketTypeCode: z.string(),
  slotId: z.string(),
  slotStartTime: z.string().regex(/^\d{2}:\d{2}$/),
  slotEndTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  slotDate: z.string().regex(/^\d{4}-\d{2}-\d{2}/),
  unitPriceIdr: z.number().int().min(0),
});
export type WristbandAddonItem = z.infer<typeof WristbandAddonItemSchema>;

export const InitiateWristbandAddonRequestSchema = z.object({
  items: z.array(WristbandAddonItemSchema).min(1),
  totalIdr: z.number().int().min(1),
  paymentMethod: KioskPaymentMethodSchema,
  edcBank: z.string().optional(),
  successRedirectUrl: z.string().url(),
});
export type InitiateWristbandAddonRequest = z.infer<typeof InitiateWristbandAddonRequestSchema>;

export const InitiateWristbandAddonResponseSchema = z.object({
  transactionId: z.string(),
  transactionNumber: z.string(),
  totalIdr: z.number(),
  status: z.enum(['PENDING', 'PROCESSING']),
  paymentMethod: KioskPaymentMethodSchema,
  paymentDetails: WalkUpPaymentDetailsSchema,
  wristbandId: z.string(),
});
export type InitiateWristbandAddonResponse = z.infer<typeof InitiateWristbandAddonResponseSchema>;

// ============================================================================
// Admin Visitor Support — kiosk user mapping
// ============================================================================

/**
 * One entry per kiosk device: maps the kiosk-service Kiosk.id to the
 * identity-service KIOSK user id. Consumed by identity-service to resolve
 * kiosk names for the admin kiosk visitors list, and by ticketing-service
 * to fetch device details for the kiosk visitor detail BFF.
 */
export const KioskUserMappingItemSchema = z.object({
  kioskId: z.string(),
  userId: z.string().nullable(),
  serialNumber: z.string().optional(),
  location: z.string().optional(),
  zoneId: z.string().optional(),
  deviceType: z.string().optional(),
  status: z.string().optional(),
  capabilities: z.array(z.string()).optional(),
  lastHeartbeatAt: z.string().datetime().nullable().optional(),
  activatedAt: z.string().datetime().nullable().optional(),
});
export type KioskUserMappingItem = z.infer<typeof KioskUserMappingItemSchema>;

export const KioskUserMappingResponseSchema = z.object({
  items: z.array(KioskUserMappingItemSchema),
});
export type KioskUserMappingResponse = z.infer<typeof KioskUserMappingResponseSchema>;

// ============================================================================
// Walk-Up Wristband Issuance
// POST /walk-up/purchase/:transactionId/issue-wristbands
// ============================================================================

export const WalkUpIssuedWristbandSchema = z.object({
  wristbandId: z.string(),
  wristbandCode: z.string(),
  qrContent: z.string(),
  qrCodeBase64: z.string(),
  ticketId: z.string(),
  visitorName: z.string().nullable(),
  printStatus: z.enum(['COMPLETED', 'FALLBACK', 'FAILED']),
});
export type WalkUpIssuedWristband = z.infer<typeof WalkUpIssuedWristbandSchema>;

export const WalkUpIssueWristbandsResponseSchema = z.object({
  wristbands: z.array(WalkUpIssuedWristbandSchema),
  printedCount: z.number().int(),
  totalRequested: z.number().int(),
  errors: z.array(
    z.object({
      ticketId: z.string(),
      error: z.string(),
    }),
  ),
});
export type WalkUpIssueWristbandsResponse = z.infer<typeof WalkUpIssueWristbandsResponseSchema>;

/**
 * Walk-up identity form schema (FE-only; the walk-up initiate request uses
 * InitiateWalkUpRequestSchema). Carries no custom messages by design -- the
 * kiosk installs a locale-aware Zod error map (apps/kiosk/src/i18n/zod-i18n.ts)
 * that localizes the standard `required` / `email` issues, mirroring apps/web
 * (#460) and apps/admin (#462).
 */
export const WalkUpIdentitySchema = z.object({
  customerName: z.string().trim().min(1),
  customerEmail: z.string().trim().min(1).email(),
});
export type WalkUpIdentityValues = z.infer<typeof WalkUpIdentitySchema>;

export * from './wristband-identity';
export * from './wristband-transfer';
